import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, CreditCardIcon, TruckIcon } from '@heroicons/react/24/outline';
import { request } from '../utils/request';
import { 
  CartItem, 
  getCurrentCartState, 
  getCartTotal,
  initializeCart,
  clearCart
} from '../utils/cartManager';
import { 
  countries, 
  statesByCountry, 
  getCountryByCode, 
  getStatesByCountryCode,
  searchStates 
} from '../utils/addressData';
import { calculateShippingAndTax } from '../utils/computeUtils';

// 地址接口类型定义
interface Address {
  id: number;
  type: string;
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  countryCode?: string;
  phone?: string;
  isDefault: boolean;
}

const CheckoutPage: React.FC = () => {
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string>('');
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isLoadingCart, setIsLoadingCart] = useState(false);
  const [shipping, setShipping] = useState(5.99);
  const [tax, setTax] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    addressLine1: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    countryCode: 'US'
  });
  
  // 州/省搜索相关状态
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [selectedStateIndex, setSelectedStateIndex] = useState(-1);

  const subtotal = getCartTotal();
  const discount = couponApplied ? couponDiscount : 0;
  const total = subtotal + shipping + tax - discount;

  // 计算运费和税费的函数
  const handleCalculateShippingAndTax = async (subtotal: number) => {
    setIsCalculating(true);
    try {
      const result = await calculateShippingAndTax(subtotal);
      setShipping(result.shipping);
      setTax(result.tax);
      
      if (result.error) {
        console.error('计算运费和税费失败:', result.error);
      }
    } finally {
      setIsCalculating(false);
    }
  };

  // 加载购物车数据
  const loadCartData = async () => {
    try {
      setIsLoadingCart(true);
      await initializeCart();
      const currentCartState = getCurrentCartState();
      setCartItems(currentCartState);
      
      // 计算运费和税费
      const currentSubtotal = getCartTotal();
      handleCalculateShippingAndTax(currentSubtotal);
    } catch (error) {
      console.error('加载购物车数据失败:', error);
    } finally {
      setIsLoadingCart(false);
    }
  };

  // 加载地址列表
  const loadAddresses = async () => {
    try {
      setIsLoadingAddresses(true);
      const response: any = await request({
        method: 'GET',
        url: 'addresses'
      });
      
      if (response.success && response.result) {
        setAddresses(response.result);
        // 如果有默认地址，自动选择
        const defaultAddress = response.result.find((addr: Address) => addr.isDefault);
        if (defaultAddress) {
          setSelectedAddressId(defaultAddress.id.toString());
          fillAddressForm(defaultAddress);
        }
      }
    } catch (error) {
      console.error('加载地址列表失败:', error);
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  // 填充地址表单
  const fillAddressForm = (address: Address) => {
    setShippingAddress({
      firstName: address.firstName,
      lastName: address.lastName,
      phone: address.phone || '',
      addressLine1: address.addressLine1,
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
      country: address.country,
      countryCode: address.countryCode || 'US'
    });
    setStateSearchTerm(address.state || '');
  };

  // 处理地址选择变化
  const handleAddressChange = (addressId: string) => {
    setSelectedAddressId(addressId);
    if (addressId) {
      const selectedAddress = addresses.find(addr => addr.id.toString() === addressId);
      if (selectedAddress) {
        fillAddressForm(selectedAddress);
      }
    } else {
      // 清空表单
      setShippingAddress({
        firstName: '',
        lastName: '',
        phone: '',
        addressLine1: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'United States',
        countryCode: 'US'
      });
      setStateSearchTerm('');
    }
  };

  // 处理国家选择变化
  const handleCountryChange = (countryCode: string) => {
    const selectedCountry = getCountryByCode(countryCode);
    setShippingAddress(prev => ({
      ...prev,
      countryCode: countryCode,
      country: selectedCountry?.name || '',
      state: '', // 重置州/省选择
      city: '' // 重置城市选择
    }));
    setStateSearchTerm(''); // 重置搜索词
    setShowStateDropdown(false); // 隐藏下拉框
    setSelectedStateIndex(-1); // 重置选中索引
  };

  // 处理州/省选择变化
  const handleStateChange = (stateName: string) => {
    setShippingAddress(prev => ({
      ...prev,
      state: stateName,
      city: '' // 重置城市选择
    }));
    setStateSearchTerm(stateName);
    setShowStateDropdown(false);
  };

  // 处理州/省搜索变化
  const handleStateSearchChange = (searchTerm: string) => {
    setStateSearchTerm(searchTerm);
    setShowStateDropdown(true);
    setSelectedStateIndex(-1); // 重置选中索引
    
    // 如果搜索词为空，清空选择
    if (searchTerm === '') {
      setShippingAddress(prev => ({
        ...prev,
        state: '',
        city: ''
      }));
    }
  };

  // 处理州/省键盘事件
  const handleStateKeyDown = (e: React.KeyboardEvent) => {
    const filteredStates = getFilteredStates();
    
    if (!showStateDropdown || filteredStates.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedStateIndex(prev => 
          prev < filteredStates.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedStateIndex(prev => 
          prev > 0 ? prev - 1 : filteredStates.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedStateIndex >= 0 && selectedStateIndex < filteredStates.length) {
          handleStateChange(filteredStates[selectedStateIndex].name);
        }
        break;
      case 'Escape':
        setShowStateDropdown(false);
        setSelectedStateIndex(-1);
        break;
    }
  };

  // 过滤州/省数据
  const getFilteredStates = () => {
    return searchStates(shippingAddress.countryCode || '', stateSearchTerm);
  };

  // 组件加载时获取购物车数据和地址列表
  useEffect(() => {
    loadCartData();
    loadAddresses();

    // 从本地缓存读取 COUPON
    try {
      const cachedCoupon = localStorage.getItem('COUPON');
      console.log('Cached coupon:', cachedCoupon);
      if (cachedCoupon?.trim()) {
        console.log('Cached coupon:', cachedCoupon);
        setCouponCode(cachedCoupon);
        applyCouponCode(cachedCoupon);
      }
    } catch (e) {
      console.warn('Failed to read cached coupon:', e);
    }
  }, []);

  const applyCouponCode = async (code: string) => {
    try {
      console.log('Applying coupon code:', code);
      const data: any = await request({
        url: `coupon/check?code=${code}`,
        method: 'GET'
      });

      if (data.success && data.result) {
        const { discount, type } = data.result;
        
        // Recalculate subtotal here to ensure we have the latest value
        const currentSubtotal = getCartTotal();

        let discountAmount = 0;
        if (type === 'percentage') {
          discountAmount = (currentSubtotal * discount) / 100;
        } else {
          discountAmount = discount;
        } 
        setCouponApplied(true);
        setCouponDiscount(discountAmount);
        return discountAmount;
      } else {
        alert(data.message || 'Invalid coupon code');
        localStorage.removeItem('COUPON');
        setCouponApplied(false);
        setCouponDiscount(0);
      }
      return 0;
    } catch (error) {
      console.error('Error applying coupon:', error);
      alert('Failed to apply coupon. Please try again.');
      return 0;
    }
  };

  const handleApplyCoupon = async () => {
    if (couponCode.trim()) {
      const discountAmount = await applyCouponCode(couponCode);
      if (discountAmount > 0) {
        // Update total price
        handleCalculateShippingAndTax(subtotal); 
      }
    }
  };

  const handleRemoveCoupon = () => {
    setCouponApplied(false);
    setCouponDiscount(0);
    setCouponCode('');
  };

  const handleSubmitOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (cartItems.length === 0) {
      alert('Your cart is empty. Please add items before placing an order.');
      return;
    }

    if (!shippingAddress.firstName || !shippingAddress.lastName || !shippingAddress.addressLine1 || 
        !shippingAddress.city || !shippingAddress.state || !shippingAddress.zipCode) {
      alert('Please fill in all required shipping information.');
      return;
    }

    try {
      // Create order and redirect to PayPal for payment processing
      // First, create the order
      const orderItems = cartItems.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        price: item.product.basePrice,
        size: item.size,
        name: item.product.name,
        color: item.color,
        code: item.code,
        image: item.customImage
      }));

      // Create address object from form data
      const addressData = {
        firstName: shippingAddress.firstName,
        lastName: shippingAddress.lastName,
        phone: shippingAddress.phone,
        addressLine1: shippingAddress.addressLine1,
        city: shippingAddress.city,
        state: shippingAddress.state,
        zipCode: shippingAddress.zipCode,
        country: shippingAddress.country,
        countryCode: shippingAddress.countryCode
      };

      const lastVisit = localStorage.getItem('FIRST_VISIT_CODE');
      let first_code = null;
      try {
          const visitData = JSON.parse(lastVisit || '{}');
          if (visitData && visitData.code && visitData.timestamp > Date.now() - 24 * 60 * 60 * 1000) {
            first_code = visitData.code;
          }
      } catch (e) {
        console.warn('Failed to parse visit data:', e);
      }

      const orderData = {
        items: orderItems,
        shippingAddressId: selectedAddressId || null,
        FIRST_VISIT_CODE:first_code,
        address: addressData,
        subtotal: subtotal,
        couponCode: couponCode,
        shipping: shipping,
        discount: couponDiscount,
        tax: tax,
        total: total,
        notes: `Order total: $${total.toFixed(2)}`
      };

      // Create order
      const orderResponse: any = await request({
        url: 'orders',
        method: 'POST',
        data: orderData
      });

      if (!orderResponse.success) {
        alert(orderResponse.message || 'Failed to create order');
        return;
      }
      
      const order = orderResponse.result;
      
      // Clear cart after successful order creation
      try {
        await clearCart();
        console.log('Cart cleared successfully after order creation');
      } catch (cartError) {
        console.error('Failed to clear cart:', cartError);
        // Don't block the order process if cart clearing fails
      } 
      // Then confirm the order and get PayPal payment URL
      const confirmResponse: any = await request({
        url: 'orders/confirm',
        method: 'POST',
        data: {
          orderId: order.id
        }
      });

      if (!confirmResponse.success) {
        alert(confirmResponse.message || 'Failed to create PayPal order');
        return;
      }

      const { approvalUrl, orderId: paypalOrderId } = confirmResponse.result;

      // Redirect to PayPal Checkout
      if (approvalUrl) {
        // Redirect directly to PayPal Checkout
        window.location.href = approvalUrl;
      } else {
        alert('Order created but PayPal payment setup failed. Please contact support.');
      }

    } catch (error: any) {
      console.error('Order submission error:', error);
      if (error.response?.data?.message) {
        alert(`Order failed: ${error.response.result.message}`);
      } else {
        alert('Failed to place order. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/cart"
            className="inline-flex items-center hover:opacity-80 mb-4"
            style={{ color: '#2e3a47' }}
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Cart
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Shipping Information</h2>
              
              {/* 地址选择下拉框 */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Select Saved Address
                  </label>
                  <Link
                    to="/account"
                    className="text-sm hover:opacity-80"
                    style={{ color: '#2e3a47' }}
                  >
                    Manage Addresses
                  </Link>
                </div>
                <select
                  value={selectedAddressId}
                  onChange={(e) => handleAddressChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                  disabled={isLoadingAddresses}
                >
                  <option value="">Choose a saved address or enter manually</option>
                  {addresses.map((address) => (
                    <option key={address.id} value={address.id.toString()}>
                      {address.firstName} {address.lastName} - {address.addressLine1}, {address.city}, {address.state} {address.zipCode}
                      {address.isDefault && ' (Default)'}
                    </option>
                  ))}
                </select>
                {isLoadingAddresses && (
                  <p className="text-sm text-gray-500 mt-1">Loading addresses...</p>
                )}
                {!isLoadingAddresses && addresses.length === 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    No saved addresses found. <Link to="/account" className="text-blue-600 hover:text-blue-800">Add an address</Link>
                  </p>
                )}
              </div>

              <form onSubmit={handleSubmitOrder} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={shippingAddress.firstName}
                      onChange={(e) => setShippingAddress({...shippingAddress, firstName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={shippingAddress.lastName}
                      onChange={(e) => setShippingAddress({...shippingAddress, lastName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      required
                      value={shippingAddress.phone}
                      onChange={(e) => setShippingAddress({...shippingAddress, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address *
                  </label>
                  <input
                    type="text"
                    required
                    value={shippingAddress.addressLine1}
                    onChange={(e) => setShippingAddress({...shippingAddress, addressLine1: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City *
                    </label>
                    <input
                      type="text"
                      required
                      value={shippingAddress.city}
                      onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Country *
                    </label>
                    <select
                      value={shippingAddress.countryCode}
                      onChange={(e) => handleCountryChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                      required
                    >
                      <option value="">Select a country</option>
                      {countries.map(country => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State/Province *
                    </label>
                    {shippingAddress.countryCode && getStatesByCountryCode(shippingAddress.countryCode).length > 0 ? (
                      <div className="relative">
                        <input
                          type="text"
                          value={stateSearchTerm}
                          onChange={(e) => handleStateSearchChange(e.target.value)}
                          onKeyDown={handleStateKeyDown}
                          onFocus={() => setShowStateDropdown(true)}
                          onBlur={() => {
                            // 延迟隐藏下拉框，允许点击选项
                            setTimeout(() => setShowStateDropdown(false), 200);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                          placeholder="Search or enter state/province"
                          required
                        />
                        
                        {/* 下拉选项 */}
                        {showStateDropdown && getFilteredStates().length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {getFilteredStates().map((state, index) => (
                              <div
                                key={state.code}
                                className={`px-3 py-2 cursor-pointer text-sm ${
                                  index === selectedStateIndex 
                                    ? '' 
                                    : 'hover:bg-gray-100'
                                }`}
                                style={index === selectedStateIndex ? { backgroundColor: '#f0f2f5', color: '#2e3a47' } : {}}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  handleStateChange(state.name);
                                }}
                                onMouseEnter={() => setSelectedStateIndex(index)}
                              >
                                {state.name}
                              </div>
                            ))}
                            {getFilteredStates().length === 0 && stateSearchTerm && (
                              <div className="px-3 py-2 text-sm text-gray-500">
                                No states found
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <input
                        type="text"
                        required
                        value={shippingAddress.state}
                        onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                        placeholder="Enter state/province"
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ZIP Code *
                    </label>
                    <input
                      type="text"
                      required
                      value={shippingAddress.zipCode}
                      onChange={(e) => setShippingAddress({...shippingAddress, zipCode: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                    />
                  </div>
                </div>
              </form>
            </div>

            {/* Payment Method */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Payment Method</h2>
              <div className="space-y-4">
                <div className="flex items-center space-x-3 p-4 border-2 rounded-lg" style={{ borderColor: '#68aeb0', backgroundColor: '#f0f2f5' }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#68aeb0' }}>
                    <span className="text-white font-bold text-sm">PP</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">PayPal</h3>
                    <p className="text-sm text-gray-600">Pay securely with your PayPal account</p>
                  </div>
                  <div className="ml-auto">
                    <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#f0f2f5', color: '#2e3a47' }}>Selected</span>
                  </div>
                </div>
                <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                  <p>• Secure payment processing</p>
                  <p>• No need to enter card details</p>
                  <p>• Instant payment confirmation</p>
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-4">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Order Summary</h2>
              
              {/* Cart Items */}
              <div className="space-y-3 mb-6">
                {isLoadingCart ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-2" style={{ borderBottomColor: '#2e3a47' }}></div>
                    <p className="text-sm text-gray-600">Loading cart items...</p>
                  </div>
                ) : cartItems.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-600">Your cart is empty</p>
                    <Link to="/products" className="hover:opacity-80 text-sm" style={{ color: '#2e3a47' }}>
                      Continue Shopping
                    </Link>
                  </div>
                ) : (
                  cartItems.map((item) => (
                    <div key={item.id} className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                        {item.customImage ? (
                          <img 
                            src={item.customImage} 
                            alt={item.product.name}
                            className="w-full h-full object-cover rounded"
                          />
                        ) : item.product.image ? (
                          <img 
                            src={item.product.image} 
                            alt={item.product.name}
                            className="w-full h-full object-cover rounded"
                          />
                        ) : (
                          <span className="text-lg">📦</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900">{item.product.name}</h4>
                        <p className="text-xs text-gray-600">Qty: {item.quantity}</p>
                        {item.size && <p className="text-xs text-gray-600">Size: {item.size}</p>}
                        {item.color && <p className="text-xs text-gray-600">Color: {item.color}</p>}
                      </div>
                      <span className="text-sm font-medium">${(item.product.basePrice * item.quantity).toFixed(2)}</span>
                    </div>
                  ))
                )}
              </div>

              {/* Coupon Code */}
              <div className="border-t border-gray-200 pt-4 mb-4">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="Coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={couponApplied}
                  />
                  {!couponApplied ? (
                    <button
                      type="button"
                      onClick={handleApplyCoupon}
                      className="px-4 py-2 text-white rounded-md transition-colors"
                      style={{ backgroundColor: '#2e3a47' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                    >
                      Apply
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleRemoveCoupon}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
                {couponApplied && (
                  <p className="text-sm text-green-600 mt-2">
                    Coupon applied! Discount: ${couponDiscount.toFixed(2)}
                  </p>
                )}
              </div>

              {/* Order Totals */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-medium">
                    {isCalculating ? '...' : `$${shipping.toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax</span>
                  <span className="font-medium">
                    {isCalculating ? '...' : `$${tax.toFixed(2)}`}
                  </span>
                </div>
                {couponApplied && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>-${couponDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold">Total</span>
                    <span className="text-lg font-bold" style={{ color: '#2e3a47' }}>${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Place Order Button */}
              <button
                onClick={handleSubmitOrder}
                className="w-full text-white py-3 px-4 rounded-lg transition-colors font-medium flex items-center justify-center space-x-2"
                style={{ backgroundColor: '#2e3a47' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
              >
                <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center">
                  <span className="font-bold text-xs" style={{ color: '#2e3a47' }}>PP</span>
                </div>
                <span>Pay with PayPal</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage; 